import moment from "moment"
import type { IconType } from "react-icons"
import { FiFileText, FiMail, FiMoreHorizontal, FiPhone, FiUsers } from "react-icons/fi"
import { FaWhatsapp } from "react-icons/fa"
import type { IFollowUpItem } from "../quotationList/quotationListTypes"
import type {
    GenericQuotationFollowUpTypeItem,
    QuotationDownloadItemModel,
    QuotationFollowUpItemModel,
    QuotationFollowUpPutBody,
    QuotationFollowUpSaveResponseModel,
} from "../../models/axiosModel"

export const INTERNAL_NOTE_FOLLOW_UP_TYPE_ID = 6

export const isInternalNoteFollowUpType = (typeId: number | string): boolean =>
    Number(typeId) === INTERNAL_NOTE_FOLLOW_UP_TYPE_ID

const normalizeStatusName = (value: string): string =>
    typeof value === "string" ? value?.trim().toLowerCase().replace(/\s+/g, " ") : value

const isInternalNoteFollowUpTypeItem = (
    typeId: number | string,
    types: GenericQuotationFollowUpTypeItem[],
): boolean => {
    if (isInternalNoteFollowUpType(typeId)) return true
    const match = types.find(
        (item) => String(item.quotation_follow_up_type_id) === String(typeId),
    )
    return normalizeFollowUpTypeName(String(match?.quotation_follow_up_type_name || "")) === "internal note"
}

export const canShowAllFollowUpTypeOptions = (
    statusId: number,
    statusName: string,
    quotationStatuses: Array<{ quotation_status_id: number; quotation_status_name?: string }> = [],
): boolean => {
    const sentStatusId = quotationStatuses.find(
        (item) => normalizeStatusName(String(item.quotation_status_name ?? "")) === "sent",
    )?.quotation_status_id
    const followUpStatusId = quotationStatuses.find(
        (item) => normalizeStatusName(String(item.quotation_status_name ?? "")) === "follow up",
    )?.quotation_status_id
    const allowedStatusIds = [sentStatusId, followUpStatusId]
        .map((id) => Number(id))
        .filter((id) => id > 0)

    if (allowedStatusIds.length > 0) {
        return allowedStatusIds.includes(statusId)
    }

    const normalizedStatusName = normalizeStatusName(statusName)
    return normalizedStatusName === "sent" || normalizedStatusName === "follow up"
}

export type FollowUpTypeVisual = {
    icon: IconType
    backgroundColor: string
    iconColor: string
}

export const ACTIVITY_TYPES = {
    internalNote: {
        label: "Internal Note",
        Icon: FiFileText,
        color: "#D97706",
        bg: "#FFFBEB",
    },
    other: {
        label: "Other",
        Icon: FiMoreHorizontal,
        color: "#64748B",
        bg: "#F1F5F9",
    },
    meeting: {
        label: "Meeting",
        Icon: FiUsers,
        color: "#DB2777",
        bg: "#FDF2F8",
    },
} as const

const toFollowUpTypeVisual = (
    Icon: IconType,
    backgroundColor: string,
    iconColor: string,
): FollowUpTypeVisual => ({
    icon: Icon,
    backgroundColor,
    iconColor,
})

const FOLLOW_UP_TYPE_VISUAL_BY_ID: Record<number, FollowUpTypeVisual> = {
    1: toFollowUpTypeVisual(FiPhone, "#EFF6FF", "#2563EB"),
    2: toFollowUpTypeVisual(FiMail, "#FAF5FF", "#7C3AED"),
    3: toFollowUpTypeVisual(ACTIVITY_TYPES.meeting.Icon, ACTIVITY_TYPES.meeting.bg, ACTIVITY_TYPES.meeting.color),
    4: toFollowUpTypeVisual(FaWhatsapp, "#F0FDF4", "#16A34A"),
    5: toFollowUpTypeVisual(ACTIVITY_TYPES.other.Icon, ACTIVITY_TYPES.other.bg, ACTIVITY_TYPES.other.color),
    6: toFollowUpTypeVisual(ACTIVITY_TYPES.internalNote.Icon, ACTIVITY_TYPES.internalNote.bg, ACTIVITY_TYPES.internalNote.color),
}

const DEFAULT_FOLLOW_UP_TYPE_VISUAL: FollowUpTypeVisual = toFollowUpTypeVisual(
    ACTIVITY_TYPES.other.Icon,
    ACTIVITY_TYPES.other.bg,
    ACTIVITY_TYPES.other.color,
)

const normalizeFollowUpTypeName = (value: string): string =>
    value.trim().toLowerCase().replace(/\s+/g, " ")

const FOLLOW_UP_TYPE_VISUAL_BY_NAME: Record<string, FollowUpTypeVisual> = {
    call: FOLLOW_UP_TYPE_VISUAL_BY_ID[1],
    email: FOLLOW_UP_TYPE_VISUAL_BY_ID[2],
    meeting: FOLLOW_UP_TYPE_VISUAL_BY_ID[3],
    whatsapp: FOLLOW_UP_TYPE_VISUAL_BY_ID[4],
    other: FOLLOW_UP_TYPE_VISUAL_BY_ID[5],
    "internal note": FOLLOW_UP_TYPE_VISUAL_BY_ID[6],
}

export const getFollowUpTypeVisual = (
    typeId: number,
    typeName?: string | null,
): FollowUpTypeVisual => {
    const byId = FOLLOW_UP_TYPE_VISUAL_BY_ID[typeId]
    if (byId) return byId

    const normalizedName = normalizeFollowUpTypeName(String(typeName || ""))
    return FOLLOW_UP_TYPE_VISUAL_BY_NAME[normalizedName] ?? DEFAULT_FOLLOW_UP_TYPE_VISUAL
}

export const normalizeFollowUps = (raw: QuotationFollowUpItemModel[] | unknown): IFollowUpItem[] => {
    if (!Array.isArray(raw)) return []

    return raw
        .map((item) => ({
            quotation_follow_up_id: String(item?.quotation_follow_up_id || "").trim(),
            quotation_follow_up_type_id: Number(item?.quotation_follow_up_type_id || 0),
            quotation_follow_up_type_name: String(item?.quotation_follow_up_type_name || "").trim(),
            follow_up_date: String(item?.follow_up_date || "").trim(),
            next_follow_up_date: item?.next_follow_up_date ?? null,
            remarks: String(item?.remarks || "").trim(),
            created_at: item?.created_at ?? null,
            created_by: item?.created_by ?? null,
        }))
        .filter((item) => item.quotation_follow_up_id.length > 0)
}

export const sortFollowUpsNewestFirst = (items: IFollowUpItem[]): IFollowUpItem[] =>
    [...items].sort((a, b) => {
        const aTime = moment(a.created_at)
        const bTime = moment(b.created_at)
        if (aTime.isValid() && bTime.isValid()) {
            return bTime.valueOf() - aTime.valueOf()
        }
        if (aTime.isValid()) return -1
        if (bTime.isValid()) return 1
        return 0
    })

export const formatActivityHistoryDate = (value?: string | null): string => {
    if (!value) return "-"
    const parsed = moment(value)
    if (!parsed.isValid()) return String(value)
    return parsed.format("D MMM, YYYY")
}

export const formatQuotationTimelineTimestamp = (value?: string | null): string => {
    if (!value) return "-"
    const parsed = moment(value)
    if (!parsed.isValid()) return String(value)
    return parsed.format("DD MMM hh:mm A").replace(" Sep ", " Sept ")
}

export const resolveQuotationDownloadedAt = (
    quotationDownloads?: QuotationDownloadItemModel[] | null,
): string | null => {
    const firstDownload = Array.isArray(quotationDownloads) ? quotationDownloads[0] : null
    const downloadedAt = firstDownload?.downloaded_at
    return downloadedAt ? String(downloadedAt).trim() : null
}

export const getTodayDateString = (): string => moment().format("YYYY-MM-DD")

export const getGenericFollowUpTypes = (
    genericResponse: unknown,
): GenericQuotationFollowUpTypeItem[] => {
    const data = (genericResponse as { data?: unknown } | null)?.data
    if (Array.isArray(data)) {
        const first = data[0] as { quotation_follow_up_type?: GenericQuotationFollowUpTypeItem[] } | undefined
        if (Array.isArray(first?.quotation_follow_up_type)) {
            return first.quotation_follow_up_type
        }
        if ((data[0] as GenericQuotationFollowUpTypeItem | undefined)?.quotation_follow_up_type_id != null) {
            return data as GenericQuotationFollowUpTypeItem[]
        }
        return []
    }
    if (data && Array.isArray((data as { quotation_follow_up_type?: GenericQuotationFollowUpTypeItem[] }).quotation_follow_up_type)) {
        return (data as { quotation_follow_up_type: GenericQuotationFollowUpTypeItem[] }).quotation_follow_up_type
    }
    return []
}

export const buildFollowUpTypeOptions = (
    types: GenericQuotationFollowUpTypeItem[],
): { label: string; value: string }[] =>
    [...types]
        .sort((a, b) => Number(a.quotation_follow_up_type_id) - Number(b.quotation_follow_up_type_id))
        .map((item) => ({
            label: String(item.quotation_follow_up_type_name || "").trim(),
            value: String(item.quotation_follow_up_type_id),
        }))
        .filter((item) => item.label && item.value)

export const buildFollowUpTypeOptionsForQuotation = (
    types: GenericQuotationFollowUpTypeItem[],
    allowAllTypes: boolean,
    lockedTypeId?: number | string,
): { label: string; value: string }[] => {
    const allOptions = buildFollowUpTypeOptions(types)
    if (allowAllTypes) return allOptions

    const internalNoteOptions = allOptions.filter((option) =>
        isInternalNoteFollowUpTypeItem(option.value, types),
    )

    if (!lockedTypeId) {
        return internalNoteOptions
    }

    const lockedOption = allOptions.find((option) => option.value === String(lockedTypeId))
    if (!lockedOption || internalNoteOptions.some((option) => option.value === lockedOption.value)) {
        return internalNoteOptions
    }

    return [...internalNoteOptions, lockedOption].sort(
        (a, b) => Number(a.value) - Number(b.value),
    )
}

const extractFollowUpItemFromSaveResponse = (
    response: QuotationFollowUpSaveResponseModel,
): QuotationFollowUpItemModel | null => {
    const data = response?.data
    if (!data?.quotation_follow_up_id) return null
    return data
}

export const buildFollowUpPutBody = (
    values: AddFollowUpFormValues,
    followUpId?: string,
): QuotationFollowUpPutBody => {
    const typeId = Number(values.quotation_follow_up_type_id)
    const body: QuotationFollowUpPutBody = {
        quotation_follow_up_type_id: typeId,
        follow_up_date: values.follow_up_date,
        remarks: values.remarks.trim(),
        next_follow_up_date: isInternalNoteFollowUpType(typeId)
            ? null
            : (values.next_follow_up_date || null),
    }

    if (followUpId) {
        body.quotation_follow_up_id = followUpId
    }

    return body
}

export const resolveFollowUpsAfterSave = (
    response: QuotationFollowUpSaveResponseModel,
    currentFollowUps: IFollowUpItem[],
): IFollowUpItem[] => {
    const savedItem = extractFollowUpItemFromSaveResponse(response)
    if (!savedItem) return currentFollowUps

    const normalizedItems = normalizeFollowUps([savedItem])
    const normalized = normalizedItems[0]
    if (!normalized) return currentFollowUps

    const existingIndex = currentFollowUps.findIndex(
        (item) => item.quotation_follow_up_id === normalized.quotation_follow_up_id,
    )

    if (existingIndex >= 0) {
        const next = [...currentFollowUps]
        next[existingIndex] = normalized
        return sortFollowUpsNewestFirst(next)
    }

    return sortFollowUpsNewestFirst([normalized, ...currentFollowUps])
}

export const resolveFollowUpsAfterDelete = (
    followUpId: string,
    currentFollowUps: IFollowUpItem[],
): IFollowUpItem[] =>
    currentFollowUps.filter((item) => item.quotation_follow_up_id !== followUpId)

export const findSavedFollowUpChange = (
    previousFollowUps: IFollowUpItem[],
    nextFollowUps: IFollowUpItem[],
): IFollowUpItem | null => {
    const previousById = new Map(
        previousFollowUps.map((item) => [item.quotation_follow_up_id, item]),
    )

    for (const item of nextFollowUps) {
        if (!previousById.has(item.quotation_follow_up_id)) {
            return item
        }
    }

    for (const item of nextFollowUps) {
        const previous = previousById.get(item.quotation_follow_up_id)
        if (!previous) continue

        if (
            previous.quotation_follow_up_type_id !== item.quotation_follow_up_type_id
            || previous.follow_up_date !== item.follow_up_date
            || String(previous.next_follow_up_date || "") !== String(item.next_follow_up_date || "")
            || previous.remarks !== item.remarks
        ) {
            return item
        }
    }

    return null
}

export const shouldTransitionSentToFollowUpAfterFollowUpSave = (
    currentStatusId: number,
    currentStatusName: string,
    savedFollowUp: IFollowUpItem,
    quotationStatuses: Array<{ quotation_status_id: number; quotation_status_name?: string }> = [],
): boolean => {
    if (isInternalNoteFollowUpType(savedFollowUp.quotation_follow_up_type_id)) {
        return false
    }

    const sentStatusId = quotationStatuses.find(
        (item) => normalizeStatusName(String(item.quotation_status_name ?? "")) === "sent",
    )?.quotation_status_id

    if (sentStatusId && currentStatusId === Number(sentStatusId)) {
        return true
    }

    return normalizeStatusName(currentStatusName) === "sent"
}

export type AddFollowUpFormValues = {
    quotation_follow_up_type_id: string
    follow_up_date: string
    next_follow_up_date: string
    remarks: string
}

export type AddFollowUpFormErrors = Partial<Record<keyof AddFollowUpFormValues, string>>

export const createDefaultAddFollowUpFormValues = (): AddFollowUpFormValues => ({
    quotation_follow_up_type_id: "1",
    follow_up_date: getTodayDateString(),
    next_follow_up_date: "",
    remarks: "",
})

export const createAddFollowUpFormValuesFromItem = (item: IFollowUpItem): AddFollowUpFormValues => ({
    quotation_follow_up_type_id: String(item.quotation_follow_up_type_id || ""),
    follow_up_date: item.follow_up_date || getTodayDateString(),
    next_follow_up_date: item.next_follow_up_date ? String(item.next_follow_up_date) : "",
    remarks: item.remarks || "",
})

export const getMinNextFollowUpDate = (followUpDate: string): string => {
    const parsed = moment(followUpDate, "YYYY-MM-DD", true)
    if (!parsed.isValid()) return ""
    return parsed.add(1, "day").format("YYYY-MM-DD")
}

export const isNextFollowUpDateAfterFollowUpDate = (
    followUpDate: string,
    nextFollowUpDate: string,
): boolean => {
    if (!nextFollowUpDate) return true

    const followUp = moment(followUpDate, "YYYY-MM-DD", true)
    const nextFollowUp = moment(nextFollowUpDate, "YYYY-MM-DD", true)
    if (!followUp.isValid() || !nextFollowUp.isValid()) return true

    return nextFollowUp.isAfter(followUp)
}

export const validateAddFollowUpForm = (
    values: AddFollowUpFormValues,
): AddFollowUpFormErrors => {
    const errors: AddFollowUpFormErrors = {}

    if (!values.quotation_follow_up_type_id) {
        errors.quotation_follow_up_type_id = "Type is required"
    }
    if (!values.follow_up_date) {
        errors.follow_up_date = "Follow-up date is required"
    }
    if (!values.remarks.trim()) {
        errors.remarks = "Remarks are required"
    }
    if (
        !isInternalNoteFollowUpType(values.quotation_follow_up_type_id)
        && values.next_follow_up_date
        && !isNextFollowUpDateAfterFollowUpDate(values.follow_up_date, values.next_follow_up_date)
    ) {
        errors.next_follow_up_date = "Next follow-up date must be after the follow-up date"
    }

    return errors
}

export const hasAddFollowUpValidationErrors = (errors: AddFollowUpFormErrors): boolean =>
    Object.keys(errors).length > 0
